import React, { useState, useEffect } from "react";
import Modal from "@/components/modal";
import API from "@/services/api";
import toast from "react-hot-toast";
import { APP_URL } from "@/config";
import ApplicationDetailsModal from "./ApplicationDetailsModal";

export default function UserModal({ isOpen, onClose, user, fetchData }) {
  const [activeTab, setActiveTab] = useState("view");
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [emailsState, setEmailsState] = useState({
    list: [],
    loading: false,
    selectedEmail: null,
  });
  const [applicationsState, setApplicationsState] = useState({
    list: [],
    loading: false,
    selectedApplication: null,
  });

  useEffect(() => {
    if (user) {
      setFormData(user);
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === "emails" && user) {
      fetchEmails();
    }
    if (activeTab === "applications" && user) {
      fetchApplications();
    }
  }, [activeTab, user]);

  const fetchEmails = async () => {
    if (!user) return;
    const userId = user._id || user.id;
    if (!userId) return;

    try {
      setEmailsState((prev) => ({ ...prev, loading: true }));
      const response = await API.get(`/user/${userId}/emails`);

      if (!response.ok) throw response;
      if (response.data) {
        setEmailsState((prev) => ({ ...prev, list: response.data.events || [], loading: false }));
      }
    } catch (e) {
      console.error("Error fetching emails:", e);
      toast.error(e.code);
      setEmailsState((prev) => ({ ...prev, loading: false }));
    }
  };

  const fetchEmailContent = async (messageId) => {
    try {
      setEmailsState((prev) => ({ ...prev, loading: true, selectedEmail: null }));
      const response = await API.get(`/user/emails/${messageId}`);

      if (!response.ok) throw response;
      if (response.data) {
        setEmailsState((prev) => ({ ...prev, selectedEmail: response.data, loading: false }));
      }
    } catch (e) {
      console.error("Error fetching email content:", e);
      toast.error(e.code);
      setEmailsState((prev) => ({ ...prev, loading: false }));
    }
  };

  const closeEmailContent = () => {
    setEmailsState((prev) => ({ ...prev, selectedEmail: null }));
  };

  const fetchApplications = async () => {
    if (!user) return;
    const userId = user._id || user.id;
    if (!userId) return;

    try {
      setApplicationsState((prev) => ({ ...prev, loading: true }));
      const response = await API.post("/application/search", {
        candidate_id: userId,
        page: 1,
        limit: 100,
      });

      if (!response.ok) throw response;
      if (response.data) {
        setApplicationsState((prev) => ({ ...prev, list: response.data || [], loading: false }));
      }
    } catch (e) {
      console.error("Error fetching applications:", e);
      toast.error(e.code);
      setApplicationsState((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleApplicationClick = (application) => {
    setApplicationsState((prev) => ({ ...prev, selectedApplication: application }));
  };

  const closeApplicationDetails = () => {
    setApplicationsState((prev) => ({ ...prev, selectedApplication: null }));
  };

  const handleFormChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!user) return;
    const userId = user._id || user.id;
    if (!userId) return;

    try {
      setLoading(true);
      const response = await API.put(`/user/${userId}`, formData);

      if (!response.ok) throw response;
      toast.success("User updated");
      fetchData();
      onClose();
    } catch (e) {
      console.error("Error updating user:", e);
      toast.error(e.code);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      setLoading(true);
      const response = await API.remove(`/user/${user._id}`);
      if (!response.ok) throw response;
      toast.success("User deleted");
      fetchData();
      onClose();
    } catch (e) {
      console.error("Error deleting user:", e);
      toast.error(e.code);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-4xl w-full">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-800">User Details</h2>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab("view")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "view" ? "border-purple-500 text-purple-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Information
            </button>
            {user.role_candidate && (
              <button
                onClick={() => setActiveTab("applications")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "applications" ? "border-purple-500 text-purple-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Applications
              </button>
            )}
            <button
              onClick={() => setActiveTab("emails")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "emails" ? "border-purple-500 text-purple-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Emails
            </button>
            <button
              onClick={() => setActiveTab("raw")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "raw" ? "border-purple-500 text-purple-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Raw
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "raw" ? (
          <pre className="bg-gray-50 p-4 rounded-lg overflow-auto text-sm max-h-[70vh]">{JSON.stringify(user, null, 2)}</pre>
        ) : activeTab === "applications" ? (
          <div className="max-h-[70vh] overflow-auto">
            {applicationsState.loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : applicationsState.list.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No applications found for this candidate</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="text-left whitespace-nowrap text-xs font-medium text-gray-600 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3">Job Title</th>
                        <th className="px-4 py-3">Organization</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Matching Score</th>
                        <th className="px-4 py-3">Application Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {applicationsState.list.map((application) => {
                        const applicationId = application._id || application.id;
                        return (
                          <tr key={applicationId} onClick={() => handleApplicationClick(application)} className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer">
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{application.job_title || "-"}</td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{application.organization_name || "-"}</td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${
                                  application.status === "APPLIED"
                                    ? "bg-blue-100 text-blue-800"
                                    : application.status === "PRESELECTED"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : application.status === "INTERVIEW"
                                    ? "bg-purple-100 text-purple-800"
                                    : application.status === "SELECTED"
                                    ? "bg-green-100 text-green-800"
                                    : application.status === "REFUSED"
                                    ? "bg-red-100 text-red-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {application.status || "-"}
                              </span>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                              {application.global_matching_score ? `${application.global_matching_score}%` : "-"}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                              {application.application_date ? new Date(application.application_date).toLocaleDateString("fr-FR") : "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : activeTab === "emails" ? (
          <div className="max-h-[70vh] overflow-auto">
            {emailsState.loading && !emailsState.selectedEmail ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : emailsState.selectedEmail ? (
              <div>
                {/* <button onClick={closeEmailContent} className="mb-4 text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1">
                  <span>←</span> Back to emails list
                </button>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="mb-4 pb-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{emailsState.selectedEmail.subject}</h3>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">From:</span> {emailsState.selectedEmail.from || "N/A"}
                      </div>
                      <div>
                        <span className="font-medium">To:</span> {emailsState.selectedEmail.email || "N/A"}
                      </div>
                      <div>
                        <span className="font-medium">Date:</span> {emailsState.selectedEmail.date ? new Date(emailsState.selectedEmail.date).toLocaleString("fr-FR") : "N/A"}
                      </div>
                      <div>
                        <span className="font-medium">Message ID:</span> {emailsState.selectedEmail.messageId || "N/A"}
                      </div>
                      <div>
                        <span className="font-medium">Event:</span>{" "}
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            emailsState.selectedEmail.event === "delivered"
                              ? "bg-green-100 text-green-800"
                              : emailsState.selectedEmail.event === "opened"
                              ? "bg-blue-100 text-blue-800"
                              : emailsState.selectedEmail.event === "clicked"
                              ? "bg-purple-100 text-purple-800"
                              : emailsState.selectedEmail.event === "bounced"
                              ? "bg-red-100 text-red-800"
                              : emailsState.selectedEmail.event === "requests"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {emailsState.selectedEmail.event || "N/A"}
                        </span>
                      </div>
                      {emailsState.selectedEmail.tag && (
                        <div>
                          <span className="font-medium">Tag:</span>{" "}
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">{emailsState.selectedEmail.tag}</span>
                        </div>
                      )}
                      {emailsState.selectedEmail.ip && (
                        <div>
                          <span className="font-medium">IP:</span> {emailsState.selectedEmail.ip}
                        </div>
                      )}
                    </div>
                  </div>
                  {emailsState.loading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                    </div>
                  ) : (
                    <div className="prose max-w-none">
                      {emailsState.selectedEmail.htmlContent ? (
                        <iframe srcDoc={emailsState.selectedEmail.htmlContent} className="w-full h-96 border border-gray-200 rounded" title="Email Content" />
                      ) : emailsState.selectedEmail.textContent ? (
                        <pre className="whitespace-pre-wrap text-sm">{emailsState.selectedEmail.textContent}</pre>
                      ) : (
                        <p className="text-gray-500">No content available</p>
                      )}
                    </div>
                  )}
                </div> */}
              </div>
            ) : emailsState.list.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No emails found for this user</p>
              </div>
            ) : (
              <div className="space-y-2">
                {emailsState.list.map((email, idx) => (
                  <div
                    key={email.messageId || idx}
                    // onClick={() => fetchEmailContent(email.messageId)}
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:border-purple-300 hover:shadow-sm transition-all cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900 mb-1">{email.subject || "No Subject"}</h4>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{email.date ? new Date(email.date).toLocaleString("fr-FR") : "N/A"}</span>
                          <span className="text-gray-600">From: {email.from || "N/A"}</span>
                          {email.tag && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">{email.tag}</span>}
                        </div>
                      </div>
                      <div className="ml-4">
                        {email.event === "delivered" && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Delivered</span>
                        )}
                        {email.event === "opened" && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Opened</span>}
                        {email.event === "clicks" && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">Clicked</span>
                        )}
                        {email.event === "bounced" && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Bounced</span>}
                        {email.event === "sent" && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">Sent</span>}
                        {email.event === "requests" && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">Requests</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={formData.firstname ?? ""}
                  onChange={(e) => handleFormChange("firstname", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={formData.lastname ?? ""}
                  onChange={(e) => handleFormChange("lastname", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email ?? ""}
                  onChange={(e) => handleFormChange("email", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
                {formData.invitation_token && (
                  <div className="mt-2">
                    <span
                      className="underline text-blue-700 cursor-pointer text-sm hover:text-blue-800"
                      onClick={() => {
                        navigator.clipboard.writeText(`${APP_URL}/auth/invitation?token=${formData.invitation_token}`);
                        toast.success("Invitation link copied!");
                      }}
                    >
                      Copy Invitation Link
                    </span>
                  </div>
                )}
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Organisations</label>
                <input
                  type="text"
                  value={(formData.organisations || []).map((o) => o.name).join(", ") || "-"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                  readOnly
                />
              </div>
              <div className="mb-4 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Roles</label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.role_candidate ?? false}
                      onChange={(e) => handleFormChange("role_candidate", e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">Candidate</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.role_recruiter ?? false}
                      onChange={(e) => handleFormChange("role_recruiter", e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">Recruiter</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.role_agency ?? false}
                      onChange={(e) => handleFormChange("role_agency", e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">Agency</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.role_admin ?? false}
                      onChange={(e) => handleFormChange("role_admin", e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">Admin</span>
                  </label>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Created Date</label>
                <input
                  type="text"
                  value={formData.created_at ? new Date(formData.created_at).toLocaleString("fr-FR") : "-"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                  readOnly
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Login</label>
                <input
                  type="text"
                  value={formData.last_login_at ? new Date(formData.last_login_at).toLocaleString("fr-FR") : "Never"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                  readOnly
                />
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Deleting..." : "Delete"}
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>

      <ApplicationDetailsModal isOpen={!!applicationsState.selectedApplication} onClose={closeApplicationDetails} application={applicationsState.selectedApplication} />
    </Modal>
  );
}
